import axios, { AxiosResponse } from 'axios';
import { 
  LLMProvider, 
  LLMModel, 
  ChatRequest, 
  ChatResponse, 
  Conversation,
  APIResponse,
  LLMAPIResponse,
  Message
} from '../types';
import API_BASE_URL from '../config/api';

// Токен авторизации для API запросов
let authToken: string | null = null;

// Инициализация токена из localStorage при загрузке модуля
if (typeof window !== 'undefined') {
  try {
    // Проверяем прямой ключ
    const directToken = localStorage.getItem('auth_token');
    if (directToken) {
      authToken = directToken;
    } else {
      // Проверяем Zustand persist storage
      const authStoreData = localStorage.getItem('auth-store');
      if (authStoreData) {
        const parsed = JSON.parse(authStoreData);
        if (parsed.state && parsed.state.token) {
          authToken = parsed.state.token;
        }
      }
    }
  } catch (e) {
    console.warn('Не удалось инициализировать токен из localStorage:', e);
  }
}

export const setAuthToken = (token: string | null) => {
  authToken = token;
};

// Получить токен из переменной или localStorage (для синхронизации с authStore)
function getAuthToken(): string | null {
  if (authToken) {
    return authToken;
  }
  
  // Проверяем localStorage на случай, если токен был восстановлен через persist
  if (typeof window !== 'undefined') {
    // Сначала проверяем прямой ключ 'auth_token'
    let tokenFromStorage = localStorage.getItem('auth_token');
    
    // Если не найден, проверяем Zustand persist storage под ключом 'auth-store'
    if (!tokenFromStorage) {
      try {
        const authStoreData = localStorage.getItem('auth-store');
        if (authStoreData) {
          const parsed = JSON.parse(authStoreData);
          if (parsed.state && parsed.state.token) {
            tokenFromStorage = parsed.state.token;
          }
        }
      } catch (e) {
        console.warn('Не удалось прочитать токен из auth-store:', e);
      }
    }
    
    // Синхронизируем с переменной для последующих запросов
    if (tokenFromStorage && !authToken) {
      authToken = tokenFromStorage;
    }
    
    return tokenFromStorage;
  }
  
  return null;
}

// Кэшируем результат проверки готовности API
let apiReadyCache: boolean = false;
let apiReadyCheckInProgress: Promise<void> | null = null;

async function waitForApiReady(baseUrl: string, timeoutMs: number = 6000, intervalMs: number = 300): Promise<void> {
  // Если API уже готов, сразу возвращаемся
  if (apiReadyCache) return;
  
  // В продакшене пропускаем проверку health - предполагаем, что API доступен
  // Это предотвращает излишние запросы и проблемы с rate limiting
  if (import.meta.env.PROD) {
    apiReadyCache = true;
    return;
  }
  
  // Если проверка уже идёт, ждём её завершения
  if (apiReadyCheckInProgress) {
    return apiReadyCheckInProgress;
  }
  
  // Начинаем новую проверку (только в dev режиме)
  apiReadyCheckInProgress = (async () => {
    const start = Date.now();
    let attempt = 0;
    const maxAttempts = 10; // Максимум 10 попыток
    
    while (Date.now() - start < timeoutMs && attempt < maxAttempts) {
      try {
        const res = await axios.get(`${baseUrl}/api/health`, { timeout: 1500 });
        if (res.status === 200) {
          apiReadyCache = true; // Кэшируем успешный результат
          apiReadyCheckInProgress = null;
          return;
        }
      } catch (err: any) {
        // Если получили 429, прекращаем попытки - это rate limiting
        if (err?.response?.status === 429) {
          console.warn('⚠️ [waitForApiReady] Получен 429, пропускаем проверку health');
          apiReadyCache = true; // Считаем API готовым, чтобы не блокировать работу
          apiReadyCheckInProgress = null;
          return;
        }
        // Для других ошибок продолжаем попытки
      }
      
      attempt++;
      // Экспоненциальная задержка: 300ms, 600ms, 1200ms, etc.
      const delay = Math.min(intervalMs * Math.pow(2, attempt - 1), 2000);
      await new Promise(r => setTimeout(r, delay));
    }
    
    // Если не удалось проверить, считаем API готовым (чтобы не блокировать)
    console.warn('⚠️ [waitForApiReady] Таймаут проверки health, считаем API готовым');
    apiReadyCache = true;
    apiReadyCheckInProgress = null;
  })();
  
  return apiReadyCheckInProgress;
}

async function axiosWithRetry<T>(fn: () => Promise<T>, attempts: number = 4, baseDelayMs: number = 250): Promise<T> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      if (i > 0) {
        // Экспоненциальная задержка между попытками
        const delay = baseDelayMs * Math.pow(2, i - 1);
        await new Promise(r => setTimeout(r, delay));
      }
      return await fn();
    } catch (err: any) {
      lastErr = err;
      
      // Не ретраим при ошибке 429 (Too Many Requests) - это rate limiting
      if (err?.response?.status === 429) {
        const retryAfter = err?.response?.headers?.['retry-after'];
        if (retryAfter) {
          console.warn(`⏳ [axiosWithRetry] Получен 429, ожидание ${retryAfter} секунд`);
          await new Promise(r => setTimeout(r, parseInt(retryAfter) * 1000));
          // Пробуем еще раз после ожидания
          continue;
        }
        // Если нет retry-after, не ретраим
        break;
      }
      
      // Не ретраим при ошибках 4xx (кроме сетевых)
      if (err?.response?.status >= 400 && err?.response?.status < 500) {
        break;
      }
      
      // Если это не сетевая ошибка — нет смысла ретраить
      if (err?.code && !['ECONNREFUSED', 'ERR_NETWORK', 'ECONNABORTED'].includes(err.code)) {
        break;
      }
    }
  }
  throw lastErr;
}

// Класс для работы с LLM API
class LLMService {
  private baseURL: string;
  private cache: Map<string, ChatResponse> = new Map();
  private cacheTimeout = 5 * 60 * 1000; // 5 минут

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    
    // Очистка кэша каждые 10 минут
    setInterval(() => {
      this.clearExpiredCache();
    }, 10 * 60 * 1000);
  }

  /**
   * Получить список доступных моделей
   */
  async getAvailableModels(): Promise<LLMModel[]> {
    try {
      // Проверяем токен ДО любых других операций
      const token = getAuthToken();
      
      if (!token) {
        const errorMsg = '⚠️ [getAvailableModels] Токен не найден! Требуется авторизация.';
        console.warn(errorMsg);
        // Если токена нет, выбрасываем ошибку сразу, не делая запрос
        const authError = new Error('Требуется авторизация для получения списка моделей');
        (authError as any).isAuthError = true;
        throw authError;
      }
      
      await waitForApiReady(this.baseURL);
      
      // Подготавливаем заголовки с токеном авторизации
      const headers: Record<string, string> = {};
      
      // Токен уже проверен выше, просто добавляем в заголовки
      headers['Authorization'] = `Bearer ${token}`;
      
      // Логируем на проде для диагностики (первые 20 символов токена)
      const tokenPreview = token.substring(0, 20) + '...';
      console.log('🔑 [getAvailableModels] Токен найден, длина:', token.length, 'превью:', tokenPreview);
      
      // Логируем детали запроса для диагностики (на проде тоже для отладки)
      console.log('📤 [getAvailableModels] Запрос к /api/models:', {
        url: `${this.baseURL}/api/models`,
        hasAuthHeader: !!headers['Authorization'],
        authHeaderLength: headers['Authorization']?.length || 0,
        authHeaderPreview: headers['Authorization']?.substring(0, 30) + '...'
      });
      
      // Убеждаемся, что заголовки правильно передаются
      const requestConfig = { 
        timeout: 5000,
        headers
      };
      
      console.log('🔧 [getAvailableModels] Конфигурация запроса:', {
        url: `${this.baseURL}/api/models`,
        hasHeaders: !!requestConfig.headers,
        headersKeys: Object.keys(requestConfig.headers),
        hasAuth: !!requestConfig.headers['Authorization']
      });
      
      const response: AxiosResponse<APIResponse<LLMModel[]>> = await axiosWithRetry(
        () => axios.get(`${this.baseURL}/api/models`, requestConfig)
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Не удалось получить список моделей');
    } catch (error: any) {
      // Детальное логирование ошибки для диагностики
      const errorDetails = {
        message: error?.message || 'Неизвестная ошибка',
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        hasAuthHeader: !!error?.config?.headers?.['Authorization'],
        authHeaderPreview: error?.config?.headers?.['Authorization']?.substring(0, 30) || null,
        allHeaders: error?.config?.headers ? Object.keys(error?.config?.headers) : null,
        code: error?.code,
        name: error?.name,
      };
      
      // Всегда логируем ошибки, даже на продуктиве (для диагностики)
      console.error('❌ [getAvailableModels] Ошибка получения моделей:', JSON.stringify(errorDetails, null, 2));
      
      // Если ошибка 401 - это проблема авторизации, не возвращаем моковые данные
      if (error?.response?.status === 401) {
        const authErrorMsg = error?.response?.data?.error || 'Ошибка авторизации. Пожалуйста, войдите в систему.';
        console.error('🔒 [getAvailableModels] Ошибка 401 - требуется авторизация');
        const authError = new Error(authErrorMsg);
        (authError as any).isAuthError = true;
        (authError as any).status = 401;
        throw authError;
      }
      
      // Если ошибка 429 - rate limiting, не возвращаем моковые данные
      if (error?.response?.status === 429) {
        const rateLimitMsg = error?.response?.data?.error || error?.response?.data?.message || 'Превышен лимит запросов. Пожалуйста, подождите немного и попробуйте снова.';
        console.error('⏳ [getAvailableModels] Ошибка 429 - превышен лимит запросов');
        const rateLimitError = new Error(rateLimitMsg);
        (rateLimitError as any).status = 429;
        (rateLimitError as any).isRateLimit = true;
        throw rateLimitError;
      }
      
      // Если токена нет - это тоже ошибка авторизации
      if (error?.message?.includes('Требуется авторизация')) {
        const authError = new Error(error.message);
        (authError as any).isAuthError = true;
        throw authError;
      }
      
      // Для других ошибок возвращаем моковые данные как fallback
      console.warn('⚠️ [getAvailableModels] Используются моковые данные из-за ошибки:', error?.message || 'неизвестная ошибка');
      return this.getMockModels();
    }
  }

  /**
   * Отправить сообщение в LLM
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      // Проверяем кэш
      const cacheKey = this.generateCacheKey(request);
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return { ...cached, fromCache: true };
      }

      // Преобразуем запрос в формат, ожидаемый сервером
      const serverRequest = {
        message: request.message,
        llmProvider: request.provider,
        llmModel: request.model,
        conversationId: request.conversationId,
        userId: request.userId
      };

      console.log('📤 Отправка запроса на сервер:', serverRequest);

      await waitForApiReady(this.baseURL);
      
      // Подготавливаем заголовки с токеном авторизации
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response: AxiosResponse<APIResponse<ChatResponse>> = await axiosWithRetry(
        () => axios.post(
          `${this.baseURL}/api/chat`,
          serverRequest,
          {
            timeout: 30000,
            headers
          }
        )
      );

      if (response.data.success && response.data.data) {
        const chatResponse = response.data.data;
        
        // Сохраняем в кэш
        this.setCache(cacheKey, chatResponse);
        
        return chatResponse;
      }
      
      throw new Error(response.data.error || 'Ошибка при отправке сообщения');
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      const errorMessage = this.getErrorMessage(error);
      console.error('Обработанное сообщение об ошибке:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  /**
   * Получить историю диалога
   */
  async getConversationHistory(conversationId: string): Promise<Conversation> {
    try {
      await waitForApiReady(this.baseURL);
      const response: AxiosResponse<APIResponse<Conversation>> = await axiosWithRetry(
        () => axios.get(`${this.baseURL}/api/conversations/${conversationId}`, { timeout: 5000 })
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Не удалось получить историю диалога');
    } catch (error) {
      console.error('Ошибка получения истории:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Создать новый диалог
   */
  async createConversation(
    title: string, 
    llmProvider: LLMProvider, 
    llmModel: string
  ): Promise<string> {
    try {
      await waitForApiReady(this.baseURL);
      const response: AxiosResponse<APIResponse<{ conversationId: string }>> = await axiosWithRetry(
        () => axios.post(
          `${this.baseURL}/api/conversations`,
          { title, llmProvider, llmModel },
          { timeout: 10000 }
        )
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data.conversationId;
      }
      
      throw new Error(response.data.error || 'Не удалось создать диалог');
    } catch (error) {
      console.error('Ошибка создания диалога:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Получить список диалогов пользователя
   */
  async getConversations(): Promise<Conversation[]> {
    try {
      await waitForApiReady(this.baseURL);
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response: AxiosResponse<APIResponse<Conversation[]>> = await axiosWithRetry(
        () => axios.get(`${this.baseURL}/api/conversations`, { 
          timeout: 5000,
          headers 
        })
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Не удалось получить список диалогов');
    } catch (error) {
      console.error('Ошибка получения диалогов:', error);
      return []; // Возвращаем пустой массив в случае ошибки
    }
  }
  
  /**
   * Получить сообщения конкретного диалога
   */
  async getConversationMessages(conversationId: string): Promise<Message[]> {
    try {
      await waitForApiReady(this.baseURL);
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response: AxiosResponse<APIResponse<Message[]>> = await axiosWithRetry(
        () => axios.get(`${this.baseURL}/api/conversations/${conversationId}/messages`, { 
          timeout: 5000,
          headers 
        })
      );
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error(response.data.error || 'Не удалось получить сообщения');
    } catch (error) {
      console.error('Ошибка получения сообщений:', error);
      return [];
    }
  }

  /**
   * Удалить диалог
   */
  async deleteConversation(conversationId: string): Promise<void> {
    try {
      await waitForApiReady(this.baseURL);
      const response: AxiosResponse<APIResponse<void>> = await axiosWithRetry(
        () => axios.delete(`${this.baseURL}/api/conversations/${conversationId}`, { timeout: 5000 })
      );
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Не удалось удалить диалог');
      }
    } catch (error) {
      console.error('Ошибка удаления диалога:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Проверить доступность API
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseURL}/api/health`, {
        timeout: 5000
      });
      return response.status === 200;
    } catch (error) {
      console.error('API недоступно:', error);
      return false;
    }
  }

  // Приватные методы

  private generateCacheKey(request: ChatRequest): string {
    // Используем безопасное кодирование для Unicode символов (кириллица и др.)
    const encodedMessage = encodeURIComponent(request.message)
      .replace(/%([0-9A-F]{2})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    // Альтернативно, используем простой хэш на основе строки
    return `${request.provider}:${request.model}:${this.hashString(request.message)}`;
  }

  // Простая функция хэширования для создания уникального ключа
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  private getFromCache(key: string): ChatResponse | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.responseTime < this.cacheTimeout) {
      return cached;
    }
    return null;
  }

  private setCache(key: string, response: ChatResponse): void {
    this.cache.set(key, response);
  }

  private clearExpiredCache(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    entries.forEach(([key, value]) => {
      if (now - value.responseTime > this.cacheTimeout) {
        this.cache.delete(key);
      }
    });
  }

  private getErrorMessage(error: any): string {
    console.error('Полная информация об ошибке:', error);
    
    if (error.response) {
      // Сервер ответил с ошибкой
      const status = error.response.status;
      const errorData = error.response.data;
      
      console.error('📋 Данные ошибки от сервера:', errorData);
      if (errorData?.details) {
        console.error('🔍 Детали валидации:', errorData.details);
      }
      
      // Пытаемся получить детальное сообщение об ошибке
      if (errorData?.error) {
        return errorData.error;
      }
      
      if (errorData?.message) {
        return errorData.message;
      }
      
      // Специфичные сообщения для разных статусов
      switch (status) {
        case 400:
          return 'Некорректный запрос. Проверьте параметры.';
        case 401:
          return 'Ошибка авторизации. Проверьте API ключи в .env файле.';
        case 402:
          // Проверяем, является ли это ошибкой недостаточного баланса
          if (errorData?.error && errorData.error.includes('баланс')) {
            return errorData.error; // Используем сообщение от сервера
          }
          return 'Ошибка GigaChat: токен авторизации недействителен или закончились лимиты. Обновите GIGACHAT_TOKEN в .env файле. Получить токен: https://developers.sber.ru/portal/products/gigachat-api';
        case 403:
          return 'Доступ запрещен. Проверьте права доступа API.';
        case 404:
          return 'Эндпоинт не найден. Проверьте URL API.';
        case 429:
          return 'Превышен лимит запросов. Попробуйте позже.';
        case 500:
          return 'Внутренняя ошибка сервера. Проверьте логи бэкенда.';
        case 502:
        case 503:
          return 'Сервер временно недоступен. Попробуйте позже.';
        default:
          return `Ошибка сервера (${status}): ${errorData?.message || 'Неизвестная ошибка'}`;
      }
    } else if (error.request) {
      // Запрос был отправлен, но ответа не получено
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return 'Превышено время ожидания ответа (30 секунд). Сервер не отвечает.';
      }
      if (error.code === 'ECONNREFUSED') {
        return 'Не удалось подключиться к серверу. Убедитесь, что бэкенд запущен на порту 3001.';
      }
      if (error.code === 'ERR_NETWORK') {
        return 'Ошибка сети. Проверьте подключение к интернету.';
      }
      return `Сервер недоступен: ${error.message || 'Проверьте подключение к интернету и убедитесь, что бэкенд запущен.'}`;
    } else {
      // Что-то пошло не так при настройке запроса
      return `Ошибка запроса: ${error.message || 'Произошла ошибка при отправке запроса.'}`;
    }
  }

  private getMockModels(): LLMModel[] {
    return [
      {
        id: 'yandexgpt',
        name: 'YandexGPT',
        provider: 'yandex',
        providerDisplayName: 'YandexGPT',
        description: 'Основная модель Yandex для обработки текста на русском языке',
        maxTokenAnswer: 8000,
        costPerToken: 0.0001,
        isAvailable: true,
        features: ['Русский язык', 'Код', 'Творчество']
      },
      {
        id: 'GigaChat-Pro',
        name: 'GigaChat Pro',
        provider: 'gigachat',
        providerDisplayName: 'GigaChat',
        description: 'Профессиональная модель Сбер для бизнес-задач',
        maxTokenAnswer: 4000,
        costPerToken: 0.0002,
        isAvailable: true,
        features: ['Бизнес', 'Анализ', 'Русский язык']
      },
      {
        id: 'llama-3.1-sonar-small-128k-online',
        name: 'Perplexity Online',
        provider: 'perplexity',
        providerDisplayName: 'Perplexity',
        description: 'Модель с доступом к актуальной информации из интернета',
        maxTokenAnswer: 16000,
        costPerToken: 0.0005,
        isAvailable: true,
        features: ['Интернет', 'Актуальность', 'Факты']
      }
    ];
  }
}

// Экспортируем единственный экземпляр сервиса
export default new LLMService();
